import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { supabase } from './lib/supabase';
import AuthModal from './components/AuthModal';
import Navbar from './components/Navbar';
import AddScreening from './components/AddScreening';
import AddMovie from './components/AddMovie';
import ManageMovies from './components/ManageMovies';
import ManageScreenings from './components/ManageScreenings';
import TicketValidatorPage from './components/TicketValidatorPage';
import OrganizerPanel from './components/OrganizerPanel';
import HostEvents from './components/HostEvents';
import AdminPanel from './components/AdminPanel';
import { Movie, Screening } from './types';
import BookingModal from './components/BookingModal';
import { getSubdomain, normalizeLabel } from './lib/tenant';
import ContactUsModal from './components/ContactUsModal';
import PrivacyPolicy from './components/PrivacyPolicy';
import HomePage from './pages/HomePage';
import OrganizersPage from './pages/OrganizersPage';
import MoviePage from './pages/MoviePage';
import { useMovies } from './hooks/useMovies';
import { useOrganizers } from './hooks/useOrganizers';
// Wedding Pages
import WeddingLandingPage from './pages/weddings/WeddingLandingPage';
import VendorListPage from './pages/weddings/VendorListPage';
import VendorProfilePage from './pages/weddings/VendorProfilePage';
import VendorSearchPage from './pages/weddings/VendorSearchPage';
import VendorDashboard from './pages/weddings/vendor-panel/VendorDashboard';
import VendorLayout from './pages/weddings/vendor-panel/VendorLayout';
import ManageVendorProfile from './pages/weddings/vendor-panel/ManageVendorProfile';
import VendorBookings from './pages/weddings/vendor-panel/VendorBookings';
import VendorPackages from './pages/weddings/vendor-panel/VendorPackages';
import VendorInquiries from './pages/weddings/vendor-panel/VendorInquiries';
import MessagesPage from './pages/weddings/MessagesPage';
import ChatWidget from './components/chat/ChatWidget';
import WeddingNavFabs from './components/weddings/WeddingNavFabs';
import { WeddingLocationProvider } from './context/WeddingLocationContext';

function App() {
  const [session, setSession] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Data from hooks
  const { data: movies = [], refetch: refetchMovies } = useMovies();
  const { data: organizers = [] } = useOrganizers();

  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [screenings, setScreenings] = useState<Screening[]>([]);
  const [showBooking, setShowBooking] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);

  // State for continuing pending bookings
  const [pendingBookingInfo, setPendingBookingInfo] = useState<{
    screeningId: number;
    bookingId: number;
    seatIds: string[];
  } | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        if (
          location.pathname !== '/privacy-policy' &&
          !location.pathname.startsWith('/weddings')
        ) {
          navigate('/');
        }
        setShowAuthModal(false);
        setShowBooking(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  const subdomain = getSubdomain(window.location.hostname);
  const isTenant = !!subdomain;

  const tenantOrganizers = isTenant
    ? organizers.filter(o => normalizeLabel(o.name) === normalizeLabel(subdomain || ''))
    : organizers;

  const tenantLogoUrl = isTenant && tenantOrganizers.length > 0
    ? (tenantOrganizers[0].logo_url || tenantOrganizers[0].image_url)
    : undefined;
  const tenantOrganizerName = isTenant && tenantOrganizers.length > 0 ? tenantOrganizers[0].name : undefined;

  const handleNavigate = (page: string) => {
    if (page === 'home') navigate('/');
    else navigate(`/${page}`);
  };

  const handleAddScreeningClose = () => {
    navigate('/');
  };

  const handleAddMovieClose = async () => {
    navigate('/');
    refetchMovies();
  };

  const bookMovie = async (movie: Movie) => {
    const { data, error } = await supabase
      .from('screenings')
      .select('*')
      .eq('movie_id', movie.id)
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true });
    if (!error) {
      setScreenings((data || []) as Screening[]);
      setSelectedMovie(movie);
      setShowBooking(true);
    }
  };

  // Handler for continuing a pending booking from My Bookings
  const handleContinuePending = async (movie: Movie, screeningId: number, bookingId: number, seatIds: string[]) => {
    setPendingBookingInfo({ screeningId, bookingId, seatIds });
    navigate('/'); // Open modal on home, or theoretically anywhere since modal is global
    const { data, error } = await supabase
      .from('screenings')
      .select('*')
      .eq('movie_id', movie.id)
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true });
    if (!error) {
      setScreenings((data || []) as Screening[]);
      setSelectedMovie(movie);
      setShowBooking(true);
    }
  };

  return (
    <div className="app-root">
      <WeddingLocationProvider>
        <Toaster position="top-center" />

        <Navbar
          session={session}
          onSignIn={() => setShowAuthModal(true)}
          onSignOut={() => {
            supabase.auth.signOut();
            navigate('/');
            setShowAuthModal(false);
            setShowBooking(false);
          }}
          onNavigate={handleNavigate}
          tenantLogoUrl={tenantLogoUrl}
          isTenant={isTenant}
          onContinuePending={handleContinuePending}
        />

        <Routes>
          <Route path="/" element={<HomePage session={session} onBook={bookMovie} />} />
          <Route path="/organizers" element={<OrganizersPage session={session} onBook={bookMovie} />} />
          <Route path="/movie/:id" element={<MoviePage onBook={bookMovie} />} />

          <Route path="/add-screening" element={<AddScreening onClose={handleAddScreeningClose} />} />
          <Route path="/add-movie" element={<AddMovie onClose={handleAddMovieClose} />} />
          <Route path="/edit-movies" element={<ManageMovies onClose={() => navigate('/')} />} />
          <Route path="/edit-screenings" element={<ManageScreenings onClose={() => navigate('/')} />} />
          <Route path="/validate-tickets" element={<TicketValidatorPage onClose={() => navigate('/')} />} />
          <Route path="/organizer-panel" element={<OrganizerPanel onClose={() => navigate('/')} onNavigate={handleNavigate} />} />
          <Route path="/admin-panel" element={<AdminPanel onClose={() => navigate('/')} />} />
          <Route path="/host-events" element={<HostEvents onClose={() => navigate('/')} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Wedding Routes */}
          <Route path="/weddings" element={<WeddingLandingPage />} />
          <Route path="/weddings/search" element={<VendorSearchPage />} />
          <Route path="/weddings/vendors/:category" element={<VendorListPage />} />
          <Route path="/weddings/vendor/:id" element={<VendorProfilePage />} />

          {/* Vendor Portal Routes */}
          <Route path="/vendor" element={<VendorLayout />}>
            <Route path="dashboard" element={<VendorDashboard />} />
            <Route path="profile" element={<ManageVendorProfile />} />
            <Route path="bookings" element={<VendorBookings />} />
            <Route path="packages" element={<VendorPackages />} />
            <Route path="inquiries" element={<VendorInquiries />} />
          </Route>

          {/* Messages */}

          {/* Messages */}
          <Route path="/weddings/messages" element={session ? <MessagesPage /> : <HomePage session={session} onBook={bookMovie} />} />


          <Route path="*" element={<HomePage session={session} onBook={bookMovie} />} />
        </Routes>

        <AuthModal
          show={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          tenantLogoUrl={tenantLogoUrl}
          isTenant={isTenant}
          tenantOrganizerName={tenantOrganizerName}
        />

        {selectedMovie && (
          <BookingModal
            show={showBooking}
            onClose={() => {
              setShowBooking(false);
              setPendingBookingInfo(null);
            }}
            movie={selectedMovie}
            screenings={screenings}
            session={session}
            pendingBookingInfo={pendingBookingInfo}
          />
        )}

        <footer className="bg-gray-800 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">Eventador</h3>
                <p className="text-gray-400">Your one-stop solution for booking movie tickets and managing events.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li><button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">Home</button></li>
                  <li><button onClick={() => navigate('/organizers')} className="text-gray-400 hover:text-white">Organizers</button></li>
                  <li><button onClick={() => navigate('/privacy-policy')} className="text-gray-400 hover:text-white">Privacy Policy</button></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact</h3>
                <p className="text-gray-400">Email: contact@tridenforge.com</p>
                {isTenant ? (
                  <button type="button" onClick={() => setShowContactUs(true)} className="text-indigo-400 hover:text-indigo-300">Contact Us</button>
                ) : (
                  <a href="mailto:tridenforge.engineering@gmail.com" className="text-indigo-400 hover:text-indigo-300">Contact Us</a>
                )}
              </div>
            </div>
            <div className="mt-8 border-t border-gray-700 pt-8 text-center text-gray-400">
              <p>&copy; 2025 Eventador. Forged in Tridenforge. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {showContactUs && (
          <ContactUsModal
            onClose={() => setShowContactUs(false)}
            organizerName={tenantOrganizerName}
            organizerContact={isTenant && tenantOrganizers.length > 0 ? tenantOrganizers[0].contact_number || tenantOrganizers[0].contact_email : undefined}
          />
        )}

        {/* Wedding Navigation FABs - Show on wedding-related pages */}
        {session && (location.pathname.startsWith('/weddings') || location.pathname.startsWith('/vendor/')) && (
          <WeddingNavFabs />
        )}

        {session && <ChatWidget />}
      </WeddingLocationProvider>
    </div>
  );
}

export default App;
